<template>
  <van-popup
    :show="show"
    round
    teleport="body"
    class="pay-password-popup"
    :close-on-click-overlay="false"
    @update:show="handlePopupShowUpdate"
    @closed="handleClosed"
  >
    <section class="pay-password-dialog">
      <header class="pay-password-dialog__head">
        <div data-strong>{{ title }}</div>
        <button type="button" @click="handleCancel">
          <van-icon name="cross" />
        </button>
      </header>

      <div class="pay-password-dialog__body">
        <div class="pay-password-input">
          <AppMaskedPasswordField
            :model-value="password"
            class="password-field"
            :visible="showPasswordText"
            :placeholder="placeholder"
            @update:model-value="(value) => emit('update:password', value)"
          >
            <template #right-icon>
              <!-- <button type="button" class="pay-password-input__toggle" @click="showPasswordText = !showPasswordText">
                <van-icon :name="showPasswordText ? 'eye-o' : 'closed-eye'" />
              </button> -->
              <button
                type="button"
                class="auth-eye-btn"
                @click="showPasswordText = !showPasswordText"
              >
                <app-image
                  :src="showPasswordText ? eyeOpenIcon : eyeCloseIcon"
                  alt=""
                />
              </button>
            </template>
          </AppMaskedPasswordField>
        </div>

        <button
          v-if="showForgot"
          type="button"
          class="pay-password-dialog__forgot"
          @click="handleForgot"
        >
          {{ forgotText }}
        </button>

        <van-button
          block
          round
          type="primary"
          class="pay-password-dialog__submit"
          :disabled="submitDisabled"
          @click="handleConfirm"
        >
          {{ confirmText }}
        </van-button>
      </div>
    </section>
  </van-popup>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useProfileStore } from "@/stores/profile";
import { navigateWithState } from "@/utils/route-state";
import AppMaskedPasswordField from "@/components/AppMaskedPasswordField.vue";
import eyeOpenIcon from "@/assets/images/eye_open_icon.png";
import eyeCloseIcon from "@/assets/images/eye_close_icon.png";
import emailDialogIcon from "@/assets/images/homeImage/email_icon3.png";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  password: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  confirmText: {
    type: String,
    default: "",
  },
  forgotText: {
    type: String,
    default: "",
  },
  showForgot: {
    type: Boolean,
    default: false,
  },
  confirmDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:show",
  "update:password",
  "confirm",
  "cancel",
  "forgot",
  "closed",
]);

const showPasswordText = ref(false);
const showBindEmailDialog = ref(false);
const router = useRouter();
const { t } = useI18n();
const profileStore = useProfileStore();
const submitDisabled = computed(() => {
  const value = String(props.password || "").trim();
  return props.confirmDisabled || value.length < 8;
});

function handleCancel() {
  emit("cancel");
  emit("update:show", false);
}

function handleConfirm() {
  if (submitDisabled.value) {
    return;
  }
  emit("confirm");
}

function handleForgot() {
  emit("forgot");
}

function handleClosed() {
  showPasswordText.value = false;
  emit("closed");
}

function handlePopupShowUpdate(value) {
  emit("update:show", value);
}
</script>

<style scoped lang="scss">
.auth-eye-btn {
  padding: 0;
  border: 0;
  background: transparent;
  display: flex;

  .app-image {
    width: 18px;
    height: 18px;
    object-fit: contain;
  }
}

.pay-password-popup {
  width: calc(100vw - 58px);
  max-width: 372px;
  overflow: hidden;
}

.pay-password-dialog {
  background: #ffffff;
}

.pay-password-dialog__head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 58px;
  padding: 0 44px;

  [data-strong] {
    color: #111111;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  button {
    position: absolute;
    top: 50%;
    right: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #999999;
    transform: translateY(-50%);

    :deep(.van-icon) {
      font-size: 22px;
    }
  }
}

.pay-password-dialog__body {
  padding: 0 18px 22px;
}

.pay-password-input {
  :deep(.van-field) {
    padding: 0 14px;
    border: 1px solid var(--wp-color-brand-1);
    border-radius: 10px;
    background: #ffffff;
  }

  :deep(.van-field__body) {
    min-height: 44px;
  }

  :deep(.van-field__control) {
    font-size: 16px;
  }

  :deep(.van-field__control::placeholder) {
    color: #666;
  }

  :deep(.van-field__right-icon) {
    margin-left: 8px;
    color: #8c8c8c;
  }
}

.password-field {
  margin: 0;
}

.pay-password-input__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #7f7f7f;

  :deep(.van-icon) {
    font-size: 18px;
  }
}

.pay-password-dialog__forgot {
  display: block;
  margin: 10px 0 0 auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: #ff3b30;
  font-size: 14px;
  line-height: 20px;
  text-decoration: underline;
}

.pay-password-dialog__submit {
  margin-top: 32px;

  :deep(.van-button) {
    height: 46px;
    border: 0;
    background: #dcdcdc;
    color: #9f9f9f;
    box-shadow: none;
  }

  :deep(.van-button--primary.van-button--disabled) {
    opacity: 1;
    background: #dcdcdc;
    color: #9f9f9f;
  }

  :deep(.van-button--primary:not(.van-button--disabled)) {
    background: linear-gradient(
      180deg,
      var(--wp-color-brand-1) 0%,
      var(--wp-color-theme) 100%
    );
    color: #ffffff;
  }
}

.bind-email-dialog-popup {
  overflow: visible;
  background: transparent;
}

.bind-email-dialog {
  position: relative;
  width: 300px;
  padding: 20px 16px 28px;
  border: 1px solid #ffffff;
  border-radius: 16px;
  background: linear-gradient(180deg, #c5dbff 0%, #ffffff 33.08%);
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.12);
  text-align: center;
}

.bind-email-dialog__icon {
  width: 129px;
  height: auto;
}

.bind-email-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 18px;
    height: 18px;
  }
}

.bind-email-dialog h2 {
  margin: 5px 0 0;
  color: #090909;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
}

.bind-email-dialog p {
  margin: 16px 0 0;
  color: #444444;
  font-size: 16px;
  line-height: 22px;
}

.bind-email-dialog__actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.bind-email-dialog__actions button {
  flex: 1;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 30px;
  font-size: 16px;
  line-height: 40px;
}

.bind-email-dialog__cancel {
  background: var(--wp-color-button-3);
  color: var(--wp-color-theme);
}

.bind-email-dialog__confirm {
  background: var(--wp-color-theme);
  color: #ffffff;
}
</style>
